import { HTMLAttributes, SyntheticEvent, useState } from 'react';

import { DutyCode, DutyCodePeriod } from 'generated';
import { matchSorter } from 'match-sorter';
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form';

import { formatTime } from 'utils/date';

import { queries } from 'RestAPIs/rapini';

import { Grid, TextField } from '@mui/material';

import { VirtualizedAutocomplete } from 'components/shared';
import { PRIMARY } from 'constants/colors';

type Props = {
  disabled?: boolean;
  field?: ControllerRenderProps<any, string>;
  fieldState?: ControllerFieldState;
  fullWidth?: boolean;
  label: string;
  onChange?: (...event: any[]) => void;
  referenceDate?: Date;
  required?: boolean;
  value?: number;
  size?: 'small' | 'medium';
  variant?: 'standard' | 'outlined' | 'filled';
};

export type PeriodOption = DutyCodePeriod & {
  startTimeString: string;
  endTimeString: string;
};

export type Option = {
  dutyCodeId?: number;
  value?: number | string;
  label: string;
  validFrom?: string;
  validTo?: string;
  periods?: PeriodOption[];
  description?: string;
};

const DutyCodeSelector = (props: Props) => {
  const {
    disabled,
    field,
    fieldState,
    fullWidth,
    size = 'medium',
    variant = 'standard',
    label,
    onChange,
    referenceDate,
    required,
    value
  } = props;

  const { data: dutyCodes } = queries.useGetDutyCodes(
    referenceDate?.toISOString()
  );

  const [timeHighlight, setTimeHighlight] = useState<boolean>(false);

  const options =
    dutyCodes
      ?.map((dc) => ({
        dutyCodeId: dc.dutyCodeId,
        value: dc.dutyCodeId,
        validFrom: dc.validFrom,
        validTo: dc.validTo,
        label: `${dc.code || dc.dutyCode}`,
        periods: dc.periods?.map((period) => ({
          ...period,
          startTimeString: formatTime(period.startTime),
          endTimeString: formatTime(period.endTime)
        })),
        description: dc.description
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const renderOption = (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: Option
  ) => {
    const styleLi = (
      <li
        {...props}
        data-option-size={Math.max(
          ((option.periods?.length || 0) + 1) * 21,
          42
        )}
      >
        <Grid container>
          <Grid item xs={2} className="label">
            {option.label}
          </Grid>
          <Grid item xs={10} className="periods">
            {option.periods?.map((period) => (
              <div key={period.dutyCodePeriodId} className="period">
                <span style={{ display: 'inline-block', width: 50 }}>
                  {formatTime(period.startTime)}
                </span>
                <span style={{ display: 'inline-block', width: 20 }}>- </span>
                <span style={{ display: 'inline-block', width: 60 }}>
                  {formatTime(period.endTime)}
                </span>
                <span style={{ display: 'inline-block', minWidth: 100 }}>
                  {period.dutyHourType?.description}
                </span>
              </div>
            ))}
          </Grid>
        </Grid>
      </li>
    );
    return styleLi;
  };

  const filterOptions = (options: Option[], { inputValue }) => {
    const getTimeFilterRegex = (a = '', b = '') =>
      new RegExp(!a || !b ? `^${a || b}$` : `^(${a})-(${b})$`, 'gi');

    const startAndEndTimeRegex = /^(\d{2}:\d{2})\s?-\s?(\d{2}:\d{2})$/i;

    const filteredOptions = options.filter((option) => {
      const { periods } = option;

      return (periods || []).some((period) => {
        if (startAndEndTimeRegex.test(inputValue || '')) {
          const [, start, end] = startAndEndTimeRegex.exec(
            inputValue || ''
          ) || ['', '', ''];
          const time = getTimeFilterRegex(start, end);
          // A specific start and end time is provided
          return time.test(`${period.startTimeString}-${period.endTimeString}`);
        } else {
          // A single time that matches either the start or end time
          const time = getTimeFilterRegex(inputValue);
          return (
            time.test(period.startTimeString) || time.test(period.endTimeString)
          );
        }
      });
    });

    setTimeHighlight(filteredOptions.length > 0);
    return filteredOptions.length > 0
      ? filteredOptions
      : matchSorter(options, inputValue, {
          keys: ['label', 'description', 'periods.*.description']
        });
  };

  const getItemSize = (
    option: React.ReactChild | React.ReactFragment | React.ReactPortal
  ) => {
    return Math.max(42, (option as any).props['data-option-size'] || 0);
  };

  const onOptionChange = (
    event: SyntheticEvent<Element, Event>,
    value: NonNullable<string | Option> | (string | Option)[] | null
  ) => {
    if (typeof value === 'string') {
      onChange &&
        onChange(event, dutyCodes?.find((dht) => dht.dutyCodeId === +value));
    } else if (Array.isArray(value)) {
      const ids = value.map((v) => (typeof v === 'string' ? +v : v.dutyCodeId));
      onChange &&
        onChange(
          event,
          dutyCodes?.filter((dht) => ids.indexOf(dht.dutyCodeId) > -1)
        );
    } else {
      value &&
        onChange &&
        onChange(
          event,
          dutyCodes?.find((dht) => dht.dutyCodeId === value.dutyCodeId)
        );
    }
  };

  return (
    <VirtualizedAutocomplete
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          size={size}
          label={label}
          required={required}
          fullWidth={fullWidth}
          disabled={disabled}
          {...(timeHighlight && {
            sx: {
              input: {
                color: PRIMARY
              }
            }
          })}
          onChange={(
            event: SyntheticEvent<Element, Event>,
            dc?: DutyCode | DutyCode[]
          ) => {
            field?.onChange(event, dc);
            onChange && onChange(event, dc);
          }}
          error={!!fieldState?.error?.type}
          helperText={fieldState?.error?.message}
        />
      )}
      options={options}
      renderOption={renderOption}
      value={options?.find(
        ({ dutyCodeId }) => dutyCodeId === (field?.value || value)
      )}
      onChange={onOptionChange}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option?: Option, value?: Option) => {
        return option?.dutyCodeId === value?.dutyCodeId;
      }}
      getItemSize={getItemSize}
      disabled={disabled}
      isCustomHeight={true}
    />
  );
};

export default DutyCodeSelector;
